import { fInteger, fNumber } from 'common/utils/formatNumber'
import numeral from 'numeral'

/**
 * หน่วยที่ใช้กรอกจำนวน/น้ำหนักต่อประเภทย่อยในฟอร์มขาย — เก็บเป็น form state
 * เท่านั้น ไม่ได้ส่งขึ้น backend ตรง ๆ เพราะ API (`Subcategory.quantityInKg`)
 * มีแค่หน่วยน้ำหนัก (กก.) เดียว ตัวเลขที่กรอกในหน่วย "ตัว" จะถูกแปลงเป็น กก.
 * ก่อนส่งเสมอ (ดู `toKg`)
 */
export type SaleQuantityUnit = 'piece' | 'kg'

/**
 * ไซส์จากฟอร์ม step 1 (`shrimpPerKg`) มีความหมายต่างกันตาม species:
 * - กุ้ง/หอย (unit suffix "ตัว/กก."): ค่าไซส์คือจำนวนตัวต่อน้ำหนัก 1 กก.
 * - ปลา (unit suffix "กรัม/ตัว."): ค่าไซส์คือน้ำหนักเป็นกรัมต่อ 1 ตัว
 */
export interface SaleUnitConverter {
  isPiecesPerKgUnit: boolean
  sizeValue: number | null
  unitSuffix: string
}

export const buildSaleUnitConverter = (
  isPiecesPerKgUnit: boolean,
  sizeValue: string | number | null | undefined,
  unitSuffix: string
): SaleUnitConverter => {
  const parsed = typeof sizeValue === 'number' ? sizeValue : Number(sizeValue)
  return {
    isPiecesPerKgUnit,
    sizeValue: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    unitSuffix,
  }
}

/** หน่วย default ต่อ species: ปลา = ตัว, กุ้ง/หอย = กก. */
export const defaultUnit = (converter: SaleUnitConverter): SaleQuantityUnit =>
  converter.isPiecesPerKgUnit ? 'kg' : 'piece'

export const canConvert = (converter: SaleUnitConverter): boolean => converter.sizeValue !== null

/** แปลงค่าที่กรอก (ตามหน่วยที่เลือกอยู่) เป็นน้ำหนักกิโลกรัมสำหรับเก็บ/ส่งค่าจริง */
export const toKg = (
  enteredValue: number,
  unit: SaleQuantityUnit,
  converter: SaleUnitConverter
): number | null => {
  if (unit === 'kg') return enteredValue
  if (!canConvert(converter)) return null
  const size = converter.sizeValue as number
  return converter.isPiecesPerKgUnit ? enteredValue / size : (enteredValue * size) / 1000
}

/** แปลงน้ำหนักกิโลกรัม (ค่าจริงที่เก็บอยู่) กลับเป็นหน่วยที่เลือกอยู่ เพื่อแสดงผลในช่องกรอก */
export const fromKg = (
  kg: number,
  unit: SaleQuantityUnit,
  converter: SaleUnitConverter
): number | null => {
  if (unit === 'kg') return kg
  if (!canConvert(converter)) return null
  const size = converter.sizeValue as number
  return converter.isPiecesPerKgUnit ? kg * size : (kg * 1000) / size
}

/** จำนวนตัวต่อ 1 กก. ตามไซส์ — null ถ้าไม่มีไซส์ให้แปลง */
const piecesPerKg = (converter: SaleUnitConverter): number | null => {
  if (!canConvert(converter)) return null
  const size = converter.sizeValue as number
  return converter.isPiecesPerKgUnit ? size : 1000 / size
}

/**
 * แปลงราคาที่กรอก (ตามหน่วยที่เลือกอยู่) เป็นบาท/กก. — API (`Subcategory.pricePerKg`)
 * รับเฉพาะบาท/กก. จึงต้องแปลงก่อนเก็บ/ส่งเสมอ
 */
export const toPricePerKg = (
  enteredPrice: number,
  unit: SaleQuantityUnit,
  converter: SaleUnitConverter
): number | null => {
  if (unit === 'kg') return enteredPrice
  const perKg = piecesPerKg(converter)
  return perKg === null ? null : enteredPrice * perKg
}

/** แปลงราคาบาท/กก. (ค่าจริงที่เก็บอยู่) กลับเป็นราคาต่อหน่วยที่เลือกอยู่ เพื่อแสดงผลในช่องกรอก */
export const fromPricePerKg = (
  pricePerKg: number,
  unit: SaleQuantityUnit,
  converter: SaleUnitConverter
): number | null => {
  if (unit === 'kg') return pricePerKg
  const perKg = piecesPerKg(converter)
  return perKg === null ? null : pricePerKg / perKg
}

/**
 * ข้อความบรรทัดแสดงค่าประมาณของหน่วยตรงข้ามกับที่กำลังกรอกอยู่ เช่น
 * "≈ 1,200.00 กก. (ไซส์ 500 ก./ตัว)" — คืนค่า null ถ้าไม่มีไซส์ให้แปลง
 */
export const approxLabel = (
  kg: number,
  currentUnit: SaleQuantityUnit,
  converter: SaleUnitConverter
): string | null => {
  if (!canConvert(converter)) return null
  const size = converter.sizeValue as number
  const sizeText = `${fInteger(size)} ${converter.unitSuffix}`
  if (currentUnit === 'piece') {
    return `≈ ${fNumber(kg)} กก. (ไซส์ ${sizeText})`
  }
  const pieces = converter.isPiecesPerKgUnit ? kg * size : (kg * 1000) / size
  return `≈ ${fInteger(pieces)} ตัว (ไซส์ ${sizeText})`
}

export interface TotalWeightDisplay {
  value: string
  unit: 'kg' | 'ton'
}

/** น้ำหนักรวมของการ์ดสรุป: เกิน 1,000 กก. ให้แสดงเป็นตันแทน */
export const formatTotalWeight = (kg: number): TotalWeightDisplay =>
  kg > 1000
    ? { value: numeral(kg / 1000).format('0,0.[0]'), unit: 'ton' }
    : { value: fNumber(kg), unit: 'kg' }
